import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckIcon, ChevronDown, Info, Search, X, XCircle, XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
}

interface VirtualizedComboboxProps {
  options: ComboboxOption[];
  selected: string[];
  onToggle: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  className?: string;
}

const ITEM_HEIGHT = 36;
// Express the cap as a number of visible options rather than a magic pixel
// value, and let the Radix `--radix-popover-content-available-height`
// constraint on PopoverContent shrink the list further when the trigger
// sits near a viewport edge.
const MAX_VISIBLE_ITEMS = 6;
const LIST_HEIGHT = ITEM_HEIGHT * MAX_VISIBLE_ITEMS;

export function VirtualizedCombobox({
  options,
  selected,
  onToggle,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  multiple = true,
  className,
}: VirtualizedComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const lower = search.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(lower));
  }, [options, search]);

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 10,
  });

  // When the scroll container mounts (popover opens), tell the virtualizer to re-measure
  const scrollCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      if (node) {
        virtualizer.measure();
      }
    },
    [virtualizer]
  );

  const handleSelect = useCallback(
    (value: string) => {
      onToggle(value);
      if (!multiple) {
        setOpen(false);
        setSearch("");
      }
    },
    [onToggle, multiple]
  );

  // Focus the search input when popover opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    } else {
      setSearch("");
    }
  }, [open]);

  // Reset scroll position when search changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    virtualizer.measure();
  }, [search, virtualizer]);

  // Measure how many badges fit in a single line (same approach as MultiSelect)
  const wrapperRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(20);

  useEffect(() => {
    const measure = measureRef.current;
    const wrapper = wrapperRef.current;
    const controls = controlsRef.current;
    if (!measure || !wrapper || selected.length === 0) {
      setVisibleCount(20);
      return;
    }

    const calculateVisible = () => {
      const badges = Array.from(
        measure.querySelectorAll<HTMLElement>('[data-measure="badge"]')
      );
      if (badges.length === 0) return;

      const moreBadgeEl = measure.querySelector<HTMLElement>('[data-measure="more"]');
      const moreBadgeStyle = moreBadgeEl ? getComputedStyle(moreBadgeEl) : null;
      const moreBadgeWidth = moreBadgeEl
        ? moreBadgeEl.offsetWidth + (parseFloat(moreBadgeStyle!.marginLeft) || 0) + (parseFloat(moreBadgeStyle!.marginRight) || 0)
        : 80;

      const controlsWidth = controls ? controls.offsetWidth : 70;
      const availableWidth = wrapper.clientWidth - controlsWidth;
      if (availableWidth <= 0) return;

      let usedWidth = 0;
      let count = 0;

      for (const badge of badges) {
        const style = getComputedStyle(badge);
        const marginLeft = parseFloat(style.marginLeft) || 0;
        const marginRight = parseFloat(style.marginRight) || 0;
        const badgeWidth = badge.offsetWidth + marginLeft + marginRight;
        const remaining = selected.length - count;

        if (remaining > 1 && usedWidth + badgeWidth + moreBadgeWidth > availableWidth && count > 0) break;
        if (remaining === 1 && usedWidth + badgeWidth > availableWidth && count > 0) break;

        usedWidth += badgeWidth;
        count++;
      }

      setVisibleCount(Math.min(count || 1, 20));
    };

    const observer = new ResizeObserver(calculateVisible);
    observer.observe(wrapper);
    calculateVisible();

    return () => observer.disconnect();
  }, [selected]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    selected.forEach((val) => onToggle(val));
  }, [selected, onToggle]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit [&_svg]:pointer-events-auto overflow-hidden",
            className
          )}
          disabled={disabled}
        >
          {selected.length > 0 ? (
            <div ref={wrapperRef} className="flex justify-between items-center w-full overflow-hidden">
              {/* Hidden measurement container */}
              <div
                ref={measureRef}
                aria-hidden
                className="flex flex-nowrap items-center fixed overflow-hidden"
                style={{ visibility: "hidden", top: -9999, left: -9999, pointerEvents: "none" }}
              >
                {selected.slice(0, 20).map((value) => {
                  const option = options.find((o) => o.value === value);
                  return (
                    <Badge
                      key={value}
                      data-measure="badge"
                      className="shrink-0 m-1 border-foreground/10 text-foreground bg-card hover:bg-card/80"
                    >
                      {option?.label || value}
                      <XCircle className="ml-2 h-4 w-4" />
                    </Badge>
                  );
                })}
                <Badge
                  data-measure="more"
                  className="shrink-0 m-1 border-foreground/10 text-foreground bg-card hover:bg-card/80"
                >
                  {`+ ${Math.max(selected.length - 1, 1)} more`}
                </Badge>
              </div>
              {/* Visible tags */}
              <div className="flex flex-nowrap items-center flex-1 min-w-0 overflow-hidden">
                {selected.slice(0, visibleCount).map((value) => {
                  const option = options.find((o) => o.value === value);
                  return (
                    <Badge
                      key={value}
                      className="shrink-0 m-1 border-foreground/10 text-foreground bg-card hover:bg-card/80"
                    >
                      {option?.label || value}
                      <XCircle
                        className="ml-2 h-4 w-4 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggle(value);
                        }}
                      />
                    </Badge>
                  );
                })}
                {selected.length > visibleCount && (
                  <Badge className="shrink-0 m-1 bg-transparent text-foreground border-foreground/1 hover:bg-transparent">
                    {`+ ${selected.length - visibleCount} more`}
                  </Badge>
                )}
              </div>
              <div ref={controlsRef} className="flex items-center justify-between shrink-0">
                <XIcon
                  className="h-4 mx-2 cursor-pointer text-muted-foreground"
                  onClick={handleClear}
                />
                <Separator orientation="vertical" className="flex min-h-6 h-full" />
                <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full mx-auto">
              <span className="text-sm text-muted-foreground mx-3">{placeholder}</span>
              <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[260px] p-0 flex flex-col"
        align="start"
        style={{ maxHeight: "var(--radix-popover-content-available-height)" }}
      >
        {/* Search input */}
        <div className="flex items-center border-b px-3 py-2 shrink-0">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 border-0 p-0 shadow-none focus-visible:ring-0"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="ml-1 rounded-sm opacity-50 hover:opacity-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Virtualized list */}
        <div
          ref={scrollCallbackRef}
          className="flex-1 min-h-0 overflow-auto"
          style={{ maxHeight: LIST_HEIGHT }}
        >
          {filtered.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </div>
          ) : (
            <div
              style={{
                height: virtualizer.getTotalSize(),
                width: "100%",
                position: "relative",
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const option = filtered[virtualItem.index];
                const isSelected = selectedSet.has(option.value);
                return (
                  <div
                    key={option.value}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: virtualItem.size,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <button
                      type="button"
                      className={cn(
                        "flex w-full items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground",
                        isSelected && "bg-accent/50"
                      )}
                      onClick={() => handleSelect(option.value)}
                    >
                      <CheckIcon
                        className={cn(
                          "h-4 w-4 shrink-0",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="flex-1 truncate text-left">
                        {option.label}
                      </span>
                      {option.description && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <div className="shrink-0 ml-1">
                                <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs bg-gray-900 text-white text-xs px-3 py-2.5">
                              <p className="font-semibold text-sm mb-1">{option.label}</p>
                              <p>{option.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with count */}
        <div className="border-t px-3 py-1.5 text-xs text-muted-foreground shrink-0">
          {filtered.length === options.length
            ? `${options.length} items`
            : `${filtered.length} of ${options.length} items`}
        </div>
      </PopoverContent>
    </Popover>
  );
}
